/**
 * CreateGroupDialog — Modal for creating a new group conversation.
 *
 * Features:
 * - Search and select contacts from known contacts list
 * - Set group name (required) and description (optional)
 * - Selected contacts shown as chips with remove
 * - Min 2 participants to create (plus you)
 * - Creates conversation with system message on submit
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  X,
  Search,
  Users,
  Check,
  MessageCircle,
  UserPlus,
  ArrowLeft,
} from "lucide-react";
import { cn } from "@slumbr/ui";
import { getFriends, searchUsers, useUserStore } from "@slumbr/core";
import type { PublicUserProfile } from "@slumbr/core";

import { useMessengerStore } from "../stores/messenger-store";
import { KNOWN_CONTACTS, type KnownContact } from "../stores/messenger-store";

// ─── Types ──────────────────────────────────────────────────────────────────

type DialogMode = "choose" | "dm" | "group";

// ─── Constants ──────────────────────────────────────────────────────────────

const DEV_USER_ID = "dev-user";
const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

const BG_COLORS = [
  "bg-blue-500",
  "bg-green-500",
  "bg-purple-500",
  "bg-orange-500",
  "bg-pink-500",
  "bg-teal-500",
  "bg-indigo-500",
  "bg-rose-500",
];

function getColorForId(id: string): string {
  const hash = id.split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return BG_COLORS[hash % BG_COLORS.length];
}

function profileToContact(profile: PublicUserProfile): KnownContact {
  return {
    userId: profile.userId,
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
  };
}

function mergeContacts(...groups: KnownContact[][]): KnownContact[] {
  const contacts = new Map<string, KnownContact>();
  for (const group of groups) {
    for (const contact of group) {
      contacts.set(contact.userId, contact);
    }
  }
  return Array.from(contacts.values()).sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );
}

// ─── Contact Item ───────────────────────────────────────────────────────────

function ContactItem({
  contact,
  isSelected,
  onToggle,
}: {
  contact: KnownContact;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
        isSelected
          ? "bg-brand-500/10 dark:bg-brand-500/20"
          : "hover:bg-surface-2 dark:hover:bg-surface-dark-2"
      )}
    >
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
          getColorForId(contact.userId)
        )}
      >
        {contact.displayName.slice(0, 2).toUpperCase()}
      </div>
      <span className="flex-1 truncate text-sm font-medium text-gray-700 dark:text-gray-300">
        {contact.displayName}
      </span>
      {isSelected && (
        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-500">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
    </button>
  );
}

// ─── Selected Contact Chip ──────────────────────────────────────────────────

function ContactChip({
  contact,
  onRemove,
}: {
  contact: KnownContact;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-500/10 py-0.5 pl-2 pr-1 text-xs font-medium text-brand-600 dark:text-brand-400">
      {contact.displayName.split(" ")[0]}
      <button
        onClick={onRemove}
        className="flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-brand-500/20"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

// ─── Main Dialog ────────────────────────────────────────────────────────────

export function CreateGroupDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const createConversation = useMessengerStore((s) => s.createConversation);
  const conversations = useMessengerStore((s) => s.conversations);
  const currentUserId = useUserStore((s) => s.user?.id) ?? DEV_USER_ID;

  const [mode, setMode] = useState<DialogMode>("choose");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContacts, setSelectedContacts] = useState<KnownContact[]>([]);
  const [friendContacts, setFriendContacts] = useState<KnownContact[]>([]);
  const [userSearchContacts, setUserSearchContacts] = useState<KnownContact[]>([]);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (isOpen) {
      setMode("choose");
      setSearchQuery("");
      setSelectedContacts([]);
      setGroupName("");
      setGroupDescription("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (DEV_AUTH_BYPASS) {
      setFriendContacts(KNOWN_CONTACTS);
      return;
    }

    let cancelled = false;

    getFriends()
      .then((response) => {
        if (cancelled) return;
        const contacts = response.items
          .map((friend) => friend.profile)
          .filter((profile): profile is PublicUserProfile => Boolean(profile))
          .map(profileToContact);
        setFriendContacts(contacts);
      })
      .catch(() => {
        if (!cancelled) setFriendContacts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!isOpen || trimmed.length < 2) {
      setUserSearchContacts([]);
      return;
    }

    if (DEV_AUTH_BYPASS) {
      setUserSearchContacts([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      searchUsers(trimmed)
        .then((response) => {
          if (!cancelled) {
            setUserSearchContacts(response.items.map(profileToContact));
          }
        })
        .catch(() => {
          if (!cancelled) setUserSearchContacts([]);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchQuery, isOpen]);

  // Focus search when switching to contact picker modes
  useEffect(() => {
    if (mode === "dm" || mode === "group") {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [mode]);

  // Filter contacts by search, excluding self
  const filteredContacts = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const contacts = mergeContacts(friendContacts, userSearchContacts);
    return contacts.filter((c) => {
      if (c.userId === DEV_USER_ID || c.userId === currentUserId) return false;
      if (!q) return true;
      return c.displayName.toLowerCase().includes(q);
    });
  }, [searchQuery, friendContacts, userSearchContacts, currentUserId]);

  const toggleContact = useCallback(
    (contact: KnownContact) => {
      setSelectedContacts((prev) => {
        const exists = prev.some((c) => c.userId === contact.userId);
        if (exists) return prev.filter((c) => c.userId !== contact.userId);
        if (mode === "dm") return [contact]; // DMs: only 1 contact
        return [...prev, contact];
      });
    },
    [mode]
  );

  const removeContact = useCallback((userId: string) => {
    setSelectedContacts((prev) => prev.filter((c) => c.userId !== userId));
  }, []);

  const handleCreateDM = useCallback(() => {
    if (selectedContacts.length !== 1) return;
    const contact = selectedContacts[0];

    // Check if DM already exists with this person
    const existingDm = conversations.find(
      (c) =>
        c.type === "direct" &&
        c.participants.some((p) => p.userId === contact.userId)
    );

    if (existingDm) {
      onCreated(existingDm.id);
      onClose();
      return;
    }

    const id = createConversation([contact.userId], {
      isGroup: false,
      contacts: [contact],
    });
    onCreated(id);
    onClose();
  }, [selectedContacts, conversations, createConversation, onCreated, onClose]);

  const handleCreateGroup = useCallback(() => {
    if (selectedContacts.length < 1 || !groupName.trim()) return;

    const id = createConversation(
      selectedContacts.map((c) => c.userId),
      {
        name: groupName.trim(),
        description: groupDescription.trim() || undefined,
        isGroup: true,
        contacts: selectedContacts,
      }
    );
    onCreated(id);
    onClose();
  }, [
    selectedContacts,
    groupName,
    groupDescription,
    createConversation,
    onCreated,
    onClose,
  ]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className="relative z-10 mx-4 flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-surface-1 shadow-2xl dark:bg-surface-dark-1"
        style={{ animation: "slideUp 200ms ease-out" }}
      >
        {/* ─── Choose Mode ─────────────────────────────────────────── */}
        {mode === "choose" && (
          <>
            <div className="flex items-center justify-between border-b border-surface-3/50 px-4 py-3 dark:border-surface-dark-3/50">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
                New Conversation
              </h3>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => setMode("dm")}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10">
                  <MessageCircle className="h-5 w-5 text-brand-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Direct Message
                  </p>
                  <p className="text-xs text-gray-500">
                    Start a private conversation
                  </p>
                </div>
              </button>
              <button
                onClick={() => setMode("group")}
                className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/10">
                  <Users className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    New Group
                  </p>
                  <p className="text-xs text-gray-500">
                    Create a group with multiple people
                  </p>
                </div>
              </button>
            </div>
          </>
        )}

        {/* ─── DM Mode ─────────────────────────────────────────────── */}
        {mode === "dm" && (
          <>
            <div className="flex items-center gap-2 border-b border-surface-3/50 px-3 py-3 dark:border-surface-dark-3/50">
              <button
                onClick={() => setMode("choose")}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <h3 className="flex-1 text-base font-semibold text-gray-800 dark:text-gray-200">
                New Direct Message
              </h3>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Search */}
            <div className="border-b border-surface-3/50 px-3 py-2 dark:border-surface-dark-3/50">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg bg-surface-2 py-1.5 pl-8 pr-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-1 focus:ring-brand-500/50 dark:bg-surface-dark-2 dark:text-gray-200"
                />
              </div>
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: "300px" }}>
              {filteredContacts.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">
                  No contacts found
                </p>
              ) : (
                filteredContacts.map((contact) => (
                  <ContactItem
                    key={contact.userId}
                    contact={contact}
                    isSelected={selectedContacts.some(
                      (c) => c.userId === contact.userId
                    )}
                    onToggle={() => toggleContact(contact)}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-surface-3/50 px-4 py-3 dark:border-surface-dark-3/50">
              <button
                onClick={handleCreateDM}
                disabled={selectedContacts.length !== 1}
                className={cn(
                  "w-full rounded-xl py-2.5 text-sm font-medium transition-colors",
                  selectedContacts.length === 1
                    ? "bg-brand-500 text-white hover:bg-brand-600"
                    : "cursor-not-allowed bg-surface-2 text-gray-400 dark:bg-surface-dark-2"
                )}
              >
                {selectedContacts.length === 1
                  ? `Message ${selectedContacts[0].displayName}`
                  : "Select a contact"}
              </button>
            </div>
          </>
        )}

        {/* ─── Group Mode ──────────────────────────────────────────── */}
        {mode === "group" && (
          <>
            <div className="flex items-center gap-2 border-b border-surface-3/50 px-3 py-3 dark:border-surface-dark-3/50">
              <button
                onClick={() => setMode("choose")}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <h3 className="flex-1 text-base font-semibold text-gray-800 dark:text-gray-200">
                New Group
              </h3>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-surface-2 dark:hover:bg-surface-dark-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Group info */}
            <div className="space-y-3 border-b border-surface-3/50 px-4 py-3 dark:border-surface-dark-3/50">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Group Name *
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  placeholder="e.g. Project Team"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  maxLength={50}
                  className="w-full rounded-lg bg-surface-2 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-1 focus:ring-brand-500/50 dark:bg-surface-dark-2 dark:text-gray-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="What's this group about?"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  maxLength={120}
                  className="w-full rounded-lg bg-surface-2 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-1 focus:ring-brand-500/50 dark:bg-surface-dark-2 dark:text-gray-200"
                />
              </div>
            </div>

            {/* Selected chips */}
            {selectedContacts.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b border-surface-3/50 px-4 py-2 dark:border-surface-dark-3/50">
                {selectedContacts.map((c) => (
                  <ContactChip
                    key={c.userId}
                    contact={c}
                    onRemove={() => removeContact(c.userId)}
                  />
                ))}
              </div>
            )}

            {/* Search contacts */}
            <div className="px-3 py-2">
              <div className="relative">
                <UserPlus className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Add members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg bg-surface-2 py-1.5 pl-8 pr-3 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-1 focus:ring-brand-500/50 dark:bg-surface-dark-2 dark:text-gray-200"
                />
              </div>
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto px-2 pb-2" style={{ maxHeight: "220px" }}>
              {filteredContacts.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">
                  No contacts found
                </p>
              ) : (
                filteredContacts.map((contact) => (
                  <ContactItem
                    key={contact.userId}
                    contact={contact}
                    isSelected={selectedContacts.some(
                      (c) => c.userId === contact.userId
                    )}
                    onToggle={() => toggleContact(contact)}
                  />
                ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-surface-3/50 px-4 py-3 dark:border-surface-dark-3/50">
              <button
                onClick={handleCreateGroup}
                disabled={selectedContacts.length < 1 || !groupName.trim()}
                className={cn(
                  "w-full rounded-xl py-2.5 text-sm font-medium transition-colors",
                  selectedContacts.length >= 1 && groupName.trim()
                    ? "bg-brand-500 text-white hover:bg-brand-600"
                    : "cursor-not-allowed bg-surface-2 text-gray-400 dark:bg-surface-dark-2"
                )}
              >
                {selectedContacts.length < 1
                  ? "Select at least 1 member"
                  : !groupName.trim()
                    ? "Enter a group name"
                    : `Create Group (${selectedContacts.length + 1} members)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
